import { useMemo, useState } from "react";
import { SelectableItem } from "@/src/components/action-modals/item-selection/item-row";
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from "@tanstack/react-query";
import apiClient from "@/src/services/api/axios";
import gqlClient from "@/src/services/api/graphql.client";
import { decodeToken } from "@/src/services/api/auth.api";
import {
  CREATE_GROUP_MEMBERSHIP_MUTATION,
  CREATE_GROUP_MUTATION,
  DELETE_GROUP_MEMBERSHIP_MUTATION,
  DELETE_GROUP_MUTATION,
  INSERT_MANY_GROUP_MEMBERSHIP_MUTATION,
  DELETE_MANY_GROUP_MEMBERSHIP_BY_GROUP,
  GET_GROUP_MEMBER_COUNT,
  GET_GROUP_MEMBERS_QUERY,
  GET_GROUPS_QUERY,
  UPDATE_GROUP_MEMBERSHIP_MUTATION,
  UPDATE_GROUP_MUTATION,
} from "./groups.query";
import { DELETE_MANY_POST_BY_GROUP } from "@/src/services/api/posts/posts.query";
import {
  BlocksUserListItem,
  GroupMember,
  GroupMemberRole,
  InvitableMember,
} from "@/src/models/user.model";
import { MembershipStatus } from "@/src/constants/groups/group-membership";
import { GroupListItem } from "@/src/models/group.model";
import { fetchBlocksUsersByIds } from "@/src/services/users.blocks.api";
import { BLOCKS_ENDPOINTS } from "@/src/services/blocks/config";
import { callGroups, groupsFunctionEnabled } from "./groups-function";

export type GroupMemberAvatar = Readonly<{
  userId: string;
  profileImageUrl: string | null;
  name: string;
}>;

export const useGetUsers = (searchTerm: string = "") => {
  return useInfiniteQuery({
    queryKey: ["getUsers", searchTerm?.trim()],
    initialPageParam: 0,
    queryFn: async ({ pageParam = 0 }) => {
      const currentOrgId = (await decodeToken())?.org_id;

      // v4 user list — `POST /iam/v4/iam/users`. `EXPO_PUBLIC_GET_USERS` pointed
      // at `/idp/v1/Iam/GetUsers`, which now 404s `Application_Not_Found`; the
      // endpoint is no longer env-configurable because the path is part of the v4
      // contract, not deployment config. `projectKey` moved to the `x-blocks-key`
      // header and the org filter key is `org_id` — same as the other list call
      // sites in `services/users.blocks.api.ts`.
      return apiClient
        .post(BLOCKS_ENDPOINTS.users, {
          page: pageParam,
          pageSize: 10,
          filter: {
            name: searchTerm?.trim() || "",
            email: "",
            status: { active: true, inactive: false },
            ...(currentOrgId && { org_id: currentOrgId }),
          },
        })
        .then((res) => {
          const users = res.data.data.map(
            (user: BlocksUserListItem): InvitableMember => ({
              itemId: user.itemId,
              userName: user.userName ?? "Unknown",
              firstName: user.firstName ?? "",
              lastName: user.lastName ?? "",
              displayName:
                user.firstName || user.lastName
                  ? `${user.firstName} ${user.lastName}`.trim()
                  : user.userName,
              profileImageUrl: user.profileImageUrl,
              email: user.email,
            }),
          );

          return {
            users,
            currentPage: pageParam as number,
            hasMore: res.data.data.length > 0,
            count: res.data.totalCount,
          };
        });
    },
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.currentPage + 1 : undefined,
    // Each search term is its own query key; without this the picker blanks
    // to "no matches" for a beat on every keystroke while the new term loads.
    placeholderData: keepPreviousData,
  });
};

/**
 * Paged group list.
 *
 * `options.where` narrows the query server-side. Callers that need a subset must
 * use it rather than filtering the pages afterwards: `pageSize` is 4, so a
 * client-side filter would routinely render an empty first page while the groups
 * it wants sit on page three.
 *
 * The `where` is part of the query key, so a narrowed list never gets served
 * from — or written over — the unfiltered one.
 */
export const useGetGroups = (
  pageSize = 4,
  options?: {
    where?: Record<string, unknown>;
    enabled?: boolean;
    /** Keep showing the last result while a changed `where` (e.g. a search
     *  term) loads, instead of dropping to empty. */
    keepPrevious?: boolean;
  },
) => {
  const where = options?.where;
  return useInfiniteQuery({
    queryKey: where ? ["getGroups", pageSize, where] : ["getGroups"],
    queryFn: async ({ pageParam = 1 }) => {
      const client = await gqlClient();
      // `GET_GROUPS_QUERY` declares `$where: groupFilterInput`; `getgroups` has
      // no `filter` argument, so the `filter: "{}"` this used to send was an
      // undeclared variable the gateway dropped on the floor.
      const result = await client.request(GET_GROUPS_QUERY, {
        where,
        order: [],
        pageNo: pageParam,
        pageSize,
      });
      return result;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) =>
      lastPage?.getgroups?.hasNextPage ? allPages.length + 1 : undefined,
    enabled: options?.enabled ?? true,
    placeholderData: options?.keepPrevious ? keepPreviousData : undefined,
  });
};

/**
 * Resolves group ids to names in one request.
 *
 * Posts carry only group ids — there is no group snapshot on the post — so the
 * visibility chip has to look names up. The ids are deduped and sorted into the
 * query key so that every caller asking for the same set shares one cache entry:
 * a feed of group posts must not turn into one request per card.
 */
export const useGroupNamesByIds = (groupIds: readonly string[]) => {
  const ids = [...new Set(groupIds.filter(Boolean))].sort();

  return useQuery({
    queryKey: ["groupNamesByIds", ids],
    queryFn: async () => {
      const client = await gqlClient();
      const result: any = await client.request(GET_GROUPS_QUERY, {
        where: { ItemId: { in: ids } },
        order: [],
        pageNo: 1,
        pageSize: ids.length,
      });

      const names: Record<string, string> = {};
      for (const group of result?.getgroups?.items ?? []) {
        if (group?.ItemId && group?.name) names[group.ItemId] = group.name;
      }
      return names;
    },
    enabled: ids.length > 0,
    staleTime: 1000 * 60 * 5,
  });
};

const GROUP_PICKER_PAGE_SIZE = 10;

/**
 * Paged, server-searched group list for ItemSelectorModal/List.
 *
 * `scope` is the caller's visibility rule (e.g. only joined groups) and the
 * search term is ANDed onto it — never a replacement — so searching can't
 * surface a group the caller wouldn't otherwise list.
 */
export const useGroupPickerItems = (
  scope: Record<string, unknown> = {},
  enabled = true,
) => {
  const [search, setSearch] = useState("");
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useGetGroups(GROUP_PICKER_PAGE_SIZE, {
      where: { ...scope, ...(search && { name: { contains: search } }) },
      enabled,
      keepPrevious: true,
    });

  const items: SelectableItem[] = useMemo(
    () =>
      (data?.pages ?? []).flatMap((page: any) =>
        (page?.getgroups?.items ?? []).map((group: GroupListItem) => ({
          itemId: group.ItemId,
          displayName: group.name,
          profileImageUrl: group.coverImageUrl,
        })),
      ),
    [data],
  );

  return {
    items,
    totalCount: (data?.pages?.[0] as any)?.getgroups?.totalCount as
      | number
      | undefined,
    onSearchChange: setSearch,
    onEndReached: () => {
      if (hasNextPage && !isFetchingNextPage) fetchNextPage();
    },
    isFetchingMore: isFetchingNextPage,
  };
};

/**
 * Selected group ids → chip items, resolved by id rather than looked up in a
 * loaded page of groups (which only ever held the first page, so any group
 * past it silently dropped out of the chips).
 */
export const useSelectedGroupItems = (
  groupIds: readonly string[] | undefined,
): SelectableItem[] => {
  const ids = groupIds ?? [];
  const { data: names } = useGroupNamesByIds(ids);
  return useMemo(
    () =>
      ids
        .filter((id) => names?.[id])
        .map((id) => ({ itemId: id, displayName: names![id] })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ids.join(","), names],
  );
};

export const useGetLatestGroups = (pageSize = 5) => {
  return useQuery({
    queryKey: ["getLatestGroups", pageSize],
    queryFn: async () => {
      const client = await gqlClient();
      const result: any = await client.request(GET_GROUPS_QUERY, {
        filter: "{}",
        order: [{ field: "CreatedDate", direction: "DESC" }],
        pageNo: 1,
        pageSize,
      });
      return (result?.getgroups?.items ?? []) as GroupListItem[];
    },
  });
};

export const useGetUserGroupMemberships = (userId?: string) => {
  return useQuery({
    queryKey: ["getUserGroupMemberships", userId],
    queryFn: async () => {
      const client = await gqlClient();
      const result: any = await client.request(GET_GROUP_MEMBERS_QUERY, {
        where: {
          userId: { eq: userId },
          status: { eq: MembershipStatus.ACTIVE },
        },
        order: [],
        pageNo: 1,
        pageSize: 200,
      });
      return (
        (result?.getgroupmemberships?.items ?? []) as { groupId: string }[]
      )
        .map((membership) => membership.groupId)
        .filter(Boolean);
    },
    enabled: !!userId,
  });
};

/**
 * Active members' emails across one or more groups, deduplicated. For a
 * broadcast (e.g. a Group-visibility news email) that must not reach people
 * outside the group — unlike `fetchAllUserEmails`, which is org-wide.
 *
 * Reads `groupUserSnapshot.email` off the membership record directly rather
 * than resolving `userId` through `fetchBlocksUsersByIds`: the snapshot is
 * already denormalized onto the row this query returns, so there is no second
 * round trip.
 */
export const fetchGroupMemberEmails = async (
  groupIds: readonly string[],
): Promise<string[]> => {
  if (groupIds.length === 0) return [];

  const client = await gqlClient();
  const result: any = await client.request(GET_GROUP_MEMBERS_QUERY, {
    where: {
      groupId: { in: groupIds },
      status: { eq: MembershipStatus.ACTIVE },
    },
    order: [],
    pageNo: 1,
    pageSize: 1000,
  });

  const emails = (
    (result?.getgroupmemberships?.items ?? []) as {
      groupUserSnapshot?: { email?: string };
    }[]
  ).map((membership) => membership.groupUserSnapshot?.email ?? "");

  return [...new Set(emails.filter(Boolean))];
};

export const useCreateGroup = () => {
  return useMutation({
    mutationFn: async (variables: {
      name: string;
      description: string;
      coverImageUrl?: string;
      profileImageUrl?: string;
      isPrivate: boolean;
    }) => {
      const client = await gqlClient();
      return client.request(CREATE_GROUP_MUTATION, variables);
    },
  });
};

export const useCreateGroupMembership = () => {
  return useMutation({
    mutationFn: async (variables: {
      groupId: string;
      userId: string;
      role: GroupMemberRole;
      status: string;
      joinType: string;
      joinedAt: string;
      invitedBy?: string;
      groupUserSnapshot: {
        firstName: string;
        lastName: string;
        email: string;
        profileImageUrl?: string;
      };
    }) => {
      if (groupsFunctionEnabled) {
        // The server decides role and status; the client only says what it wants.
        const snapshot = variables.groupUserSnapshot;
        if (variables.role === "Admin") {
          await callGroups({ action: "initGroup", groupId: variables.groupId, snapshot });
          return { insertgroupmembership: { itemId: null } };
        }
        if (variables.joinType === "added_by_admin") {
          await callGroups({ action: "addMembers", groupId: variables.groupId, members: [{ userId: variables.userId, snapshot }] });
          return { insertgroupmembership: { itemId: null } };
        }
        const joined = await callGroups<{ membershipId: string; status: string }>({ action: "join", groupId: variables.groupId, snapshot });
        return { insertgroupmembership: { itemId: joined.membershipId }, status: joined.status };
      }
      const client = await gqlClient();
      return client.request(CREATE_GROUP_MEMBERSHIP_MUTATION, variables);
    },
  });
};

export type GroupMembershipInput = {
  groupId: string;
  userId: string;
  role: GroupMemberRole;
  status: string;
  joinType: string;
  joinedAt: string;
  invitedBy?: string;
  groupUserSnapshot: {
    userId?: string;
    firstName: string;
    lastName: string;
    email: string;
    displayName?: string;
    profileImageUrl?: string;
  };
};

export type InsertManyResult = {
  acknowledged?: boolean;
  totalImpactedData?: number;
  message?: string;
  itemIds?: string[];
};

/**
 * Adds every selected member in one request.
 *
 * Rejects on a short write rather than resolving quietly: the gateway reports
 * `totalImpactedData`, so if fewer rows landed than were sent the caller hears
 * about it instead of the UI claiming success. `itemIds` is returned so a caller
 * can tell which memberships exist after a partial write.
 */
export const useCreateGroupMembershipsBulk = () => {
  return useMutation({
    mutationFn: async (
      input: GroupMembershipInput[],
    ): Promise<InsertManyResult> => {
      if (input.length === 0) {
        return { acknowledged: true, totalImpactedData: 0, itemIds: [] };
      }

      if (groupsFunctionEnabled) {
        const groupId = input[0].groupId;
        const { added } = await callGroups<{ added: number }>({ action: "addMembers", groupId,
          members: input.map((m) => ({ userId: m.userId, snapshot: m.groupUserSnapshot })) });
        return { acknowledged: true, totalImpactedData: added };
      }
      const client = await gqlClient();
      const data = await client.request<{
        insertManygroupmembership: InsertManyResult;
      }>(INSERT_MANY_GROUP_MEMBERSHIP_MUTATION, { input });

      const result = data.insertManygroupmembership ?? {};

      if (result.acknowledged === false) {
        throw new Error(result.message ?? "Bulk membership insert failed");
      }

      const written = result.totalImpactedData ?? 0;
      if (written < input.length) {
        throw new Error(
          result.message ??
            `Added ${written} of ${input.length} members; the rest were not saved`,
        );
      }

      return result;
    },
  });
};

export const useRemoveGroupMembership = () => {
  return useMutation({
    mutationFn: async (membershipId: string) => {
      if (groupsFunctionEnabled) return callGroups({ action: "remove", membershipId });
      const client = await gqlClient();
      return client.request(DELETE_GROUP_MEMBERSHIP_MUTATION, {
        itemId: membershipId,
      });
    },
  });
};

export const useDeleteGroup = () => {
  return useMutation({
    mutationFn: async (groupId: string) => {
      const client = await gqlClient();

      // 1. Delete every post shared in this group (bulk).
      await client.request(DELETE_MANY_POST_BY_GROUP, { groupId });

      // 2. Delete every membership of this group (bulk; server-side once locked).
      if (groupsFunctionEnabled) await callGroups({ action: "deleteGroup", groupId });
      else await client.request(DELETE_MANY_GROUP_MEMBERSHIP_BY_GROUP, { groupId });

      // 3. Delete the group itself (single record, by id).
      return client.request(DELETE_GROUP_MUTATION, {
        itemId: groupId,
      });
    },
  });
};

export const useUpdateGroup = () => {
  return useMutation({
    mutationFn: async (variables: {
      itemId: string;
      name?: string;
      description?: string;
      coverImageUrl?: string;
      profileImageUrl?: string;
      isPrivate: boolean;
    }) => {
      const client = await gqlClient();
      return client.request(UPDATE_GROUP_MUTATION, variables);
    },
  });
};

export const useGetGroupById = (id: string) => {
  return useQuery({
    queryKey: ["getGroupById", id],
    queryFn: async () => {
      const client = await gqlClient();
      const result = await client.request(GET_GROUPS_QUERY, {
        where: { ItemId: { eq: `${id}` } },
      });
      return result.getgroups.items[0];
    },
    enabled: !!id,
  });
};

export const useGetGroupMembers = (
  groupId: string,
  page = 1,
  pageSize = 10,
) => {
  return useQuery({
    queryKey: ["getGroupMembers", groupId, page],
    queryFn: async () => {
      const client = await gqlClient();
      return client.request(GET_GROUP_MEMBERS_QUERY, {
        where: { groupId: { eq: groupId } },
        order: [],
        pageNo: page,
        pageSize,
      });
    },
  });
};

export const useGetGroupMemberIds = (groupId: string) => {
  return useQuery({
    queryKey: ["getGroupMemberIds", groupId],
    queryFn: async (): Promise<string[]> => {
      const client = await gqlClient();
      const result: any = await client.request(GET_GROUP_MEMBERS_QUERY, {
        where: { groupId: { eq: groupId } },
        order: [],
        pageNo: 1,
        pageSize: 1000,
      });
      const items = (result?.getgroupmemberships?.items ?? []) as {
        userId: string;
      }[];
      return items.map((item) => item.userId).filter(Boolean);
    },
    enabled: !!groupId,
  });
};

export const useUpdateGroupMembership = () => {
  return useMutation({
    mutationFn: async (variables: {
      itemId: string;
      status: MembershipStatus;
      role?: GroupMemberRole;
    }) => {
      if (groupsFunctionEnabled) {
        if (variables.role) {
          return callGroups({ action: "setRole", membershipId: variables.itemId,
            role: variables.role === "Admin" ? "Admin" : "Member" });
        }
        return callGroups({ action: variables.status === MembershipStatus.ACTIVE ? "approve" : "reject",
          membershipId: variables.itemId });
      }
      const client = await gqlClient();
      return client.request(UPDATE_GROUP_MEMBERSHIP_MUTATION, variables);
    },
  });
};

export const useGetGroupMemberAvatars = (groupId: string, limit = 3) => {
  return useQuery({
    queryKey: ["getGroupMemberAvatars", groupId, limit],
    queryFn: async (): Promise<GroupMemberAvatar[]> => {
      const client = await gqlClient();
      const result = await client.request(GET_GROUP_MEMBERS_QUERY, {
        where: {
          groupId: { eq: groupId },
          status: { eq: MembershipStatus.ACTIVE },
        },
        order: [],
        pageNo: 1,
        pageSize: limit,
      });
      const items = (result?.getgroupmemberships?.items ?? []) as {
        userId: string;
      }[];
      const userIds = items.map((item) => item.userId).filter(Boolean);
      const usersMap = await fetchBlocksUsersByIds(userIds);
      return userIds.map((userId) => {
        const user = usersMap[userId];
        return {
          userId,
          profileImageUrl: user?.profileImageUrl ?? null,
          name: user
            ? `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim()
            : "",
        };
      });
    },
    enabled: !!groupId,
  });
};

export const useGetGroupMemberCount = (groupId: string) => {
  return useQuery({
    queryKey: ["getGroupMemberCount", groupId],
    queryFn: async () => {
      const client = await gqlClient();
      const result = await client.request(GET_GROUP_MEMBERS_QUERY, {
        where: {
          groupId: { eq: groupId },
          status: { eq: MembershipStatus.ACTIVE },
        },
      });
      return result.getgroupmemberships.totalCount as number;
    },
    enabled: !!groupId,
  });
};

export const useUpdateGroupMemberCount = () => {
  const { mutateAsync: updateGroup } = useUpdateGroup();

  return useMutation({
    mutationFn: async (groupId: string) => {
      // The Function maintains group.memberCount once it owns membership writes.
      if (groupsFunctionEnabled) return null;
      const client = await gqlClient();
      const result = await client.request(GET_GROUP_MEMBERS_QUERY, {
        where: { groupId: { eq: groupId } },
      });
      const totalCount = result.getgroupmemberships.totalCount;
      return updateGroup({
        itemId: groupId,
        memberCount: totalCount,
        
      });
    },
  });
};

export const useGetMyMembership = (groupId: string, userId: string) => {
  return useQuery({
    queryKey: ["getMyMembership", groupId, userId],
    queryFn: async () => {
      const client = await gqlClient();
      const result = await client.request(GET_GROUP_MEMBERS_QUERY, {
        where: { groupId: { eq: groupId }, userId: { eq: userId } },
        order: [],
        pageNo: 1,
        pageSize: 1,
      });
      return result.getgroupmemberships?.items?.[0] ?? null;
    },
    enabled: !!groupId && !!userId,
  });
};


export const useGetAllMyMemberships = (userId: string) => {
  return useQuery({
    queryKey: ["getAllMyMemberships", userId],
    queryFn: async () => {
      const client = await gqlClient();
      const result = await client.request(GET_GROUP_MEMBERS_QUERY, {
        where: { userId: { eq: userId } },
        order: [],
        pageNo: 1,
        pageSize: 100,
      });
      return result.getgroupmemberships?.items ?? [];
    },
    enabled: !!userId,
  });
};
